// react
import { useEffect, useState } from 'react';

// mui
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	Close as IconClose,
	Delete as IconDelete,
	Rule as IconRule,
	Settings as IconSettings,
} from '@mui/icons-material';

// types
import {
	ComparisonOperator,
	DataRequestField,
	DataRequestFieldType,
	ExpectationRule,
} from '../../../@types';

// evaluator
import { normalizeExpectation } from '../../../utils/expectationEvaluator';

// Constants

const FIELD_TYPE_LABELS: Record<string, string> = {
	document: 'Document',
	yesno: 'Yes / No',
	text: 'Text',
	number: 'Number',
	date: 'Date',
	select: 'Dropdown',
};

const FIELD_TYPE_COLORS: Record<string, string> = {
	document: '#1976d2',
	yesno: '#7b1fa2',
	text: '#546e7a',
	number: '#00897b',
	date: '#e65100',
	select: '#5c6bc0',
};

/** Which operators are meaningful for each field type */
const OPERATORS_BY_FIELD_TYPE: Record<DataRequestFieldType, ComparisonOperator[]> = {
	yesno: ['eq', 'neq'],
	number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
	text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'matches'],
	select: ['eq', 'neq', 'in', 'not_in'],
	date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
	document: ['exists', 'not_exists'],
};

const OPERATOR_LABELS: Record<ComparisonOperator, string> = {
	eq: 'Equals',
	neq: 'Does not equal',
	gt: 'Greater than',
	gte: 'Greater than or equal to',
	lt: 'Less than',
	lte: 'Less than or equal to',
	between: 'Between (inclusive)',
	contains: 'Contains',
	not_contains: 'Does not contain',
	starts_with: 'Starts with',
	ends_with: 'Ends with',
	in: 'Is one of',
	not_in: 'Is not one of',
	matches: 'Matches pattern (regex)',
	exists: 'Is provided',
	not_exists: 'Is not provided',
};

/** Operators that need no value input */
const VALUE_LESS_OPS: ComparisonOperator[] = ['exists', 'not_exists'];

/** Operators that need a secondary value (upper bound) */
const SECONDARY_VALUE_OPS: ComparisonOperator[] = ['between'];

/** Operators that take a list (comma-separated or multi-select) */
const LIST_OPS: ComparisonOperator[] = ['in', 'not_in'];

// Props

export interface DialogFieldSettingsProps {
	/** The field to edit */
	field: DataRequestField;
	/** Whether the dialog is open */
	open: boolean;
	/** Called when the user saves changes; supplies the updated field object */
	onSave: (updatedField: DataRequestField) => void;
	/** Called when the dialog is dismissed without saving */
	onClose: () => void;
}

// Component

export const DialogFieldSettings = ({ field, open, onSave, onClose }: DialogFieldSettingsProps) => {
	// --- Local state (editable copy) ---
	const [local, setLocal] = useState<DataRequestField>({ ...field });
	const [rules, setRules] = useState<ExpectationRule[]>([]);
	const [optionsText, setOptionsText] = useState('');

	// Reset local state when field changes or dialog opens
	useEffect(() => {
		if (open) {
			setLocal({ ...field });
			const existingRules = normalizeExpectation(field.expectedResponse);
			// Auto-populate default "equals yes" rule for yesno fields with no rules
			if (field.fieldType === 'yesno' && existingRules.length === 0) {
				setRules([{ operator: 'eq', value: 'yes' }]);
			} else {
				setRules(existingRules);
			}
			setOptionsText(field.options?.join('\n') || '');
		}
	}, [field, open]);

	// --- Helpers ---
	const accentColor = FIELD_TYPE_COLORS[local.fieldType] || '#546e7a';
	const availableOps = OPERATORS_BY_FIELD_TYPE[local.fieldType] || [];

	// --- Field property updaters ---
	const updateProp = <K extends keyof DataRequestField>(key: K, value: DataRequestField[K]) => {
		setLocal((prev) => ({ ...prev, [key]: value }));
	};

	const handleTypeChange = (newType: DataRequestFieldType) => {
		setLocal((prev) => ({
			...prev,
			fieldType: newType,
			// Reset type-specific defaults
			...(newType === 'number' ? { unit: prev.unit || '' } : {}),
			...(newType === 'select' ? { options: prev.options || [] } : {}),
		}));
		// Clear rules that aren't valid for the new type
		const newOps = OPERATORS_BY_FIELD_TYPE[newType] || [];
		const validRules = rules.filter((r) => newOps.includes(r.operator));
		// Auto-populate default "equals yes" rule for yesno fields
		if (newType === 'yesno' && validRules.length === 0) {
			setRules([{ operator: 'eq', value: 'yes' }]);
		} else {
			setRules(validRules);
		}
	};

	// --- Rule management ---
	const addRule = () => {
		const defaultOp = availableOps[0] || 'eq';
		setRules((prev) => [...prev, { operator: defaultOp }]);
	};

	const updateRule = (index: number, patch: Partial<ExpectationRule>) => {
		setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
	};

	const removeRule = (index: number) => {
		setRules((prev) => prev.filter((_, i) => i !== index));
	};

	// --- Save ---
	const handleSave = () => {
		const options = optionsText
			.split('\n')
			.map((o) => o.trim())
			.filter((o) => o.length > 0);

		const updatedField: DataRequestField = {
			...local,
			options: local.fieldType === 'select' ? options : local.options,
			expectedResponse: rules.length > 0 ? (rules.length === 1 ? rules[0] : rules) : undefined,
		};
		onSave(updatedField);
	};

	// ==========================================================================
	// Render
	// ==========================================================================

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderTop: `4px solid ${accentColor}`,
					borderRadius: 2,
					'& .MuiTypography-root': { fontSize: '1.25rem' },
					'& .MuiFormHelperText-root': { fontSize: '1rem' },
					'& .MuiFormControlLabel-label': { fontSize: '1.25rem' },
					'& .MuiInputBase-input': { fontSize: '1.25rem' },
					'& .MuiInputLabel-root': { fontSize: '1.25rem' },
					'& .MuiButton-root': { fontSize: '1.25rem' },
					'& .MuiChip-label': { fontSize: '1rem' },
					'& .MuiSelect-select': { fontSize: '1.25rem' },
					'& .MuiMenuItem-root': { fontSize: '1.25rem' },
				},
			}}>
			{/* ---------- Title ---------- */}
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					pb: 1,
				}}>
				<IconSettings sx={{ color: accentColor }} />
				<Chip
					label={FIELD_TYPE_LABELS[local.fieldType] || local.fieldType}
					size="small"
					sx={{
						bgcolor: `${accentColor}18`,
						color: accentColor,
						fontWeight: 600,
					}}
				/>
				<Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
					Field Settings
				</Typography>
				<IconButton onClick={onClose} size="small">
					<IconClose />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ pt: 2 }}>
				<Stack spacing={2}>
					{/* ============================================================= */}
					{/* SECTION 1: Basic Properties                                    */}
					{/* ============================================================= */}
					<Box>
						<Typography
							variant="overline"
							sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
							Basic Properties
						</Typography>
						<Stack spacing={2} sx={{ mt: 1 }}>
							{/* Type + Label in one row */}
							<Stack direction="row" spacing={1.5} alignItems="flex-start">
								<FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
									<InputLabel>Field Type</InputLabel>
									<Select
										label="Field Type"
										value={local.fieldType}
										onChange={(e) => handleTypeChange(e.target.value as DataRequestFieldType)}>
										<MenuItem value="text">Text</MenuItem>
										<MenuItem value="number">Number</MenuItem>
										<MenuItem value="date">Date</MenuItem>
										<MenuItem value="select">Dropdown</MenuItem>
										<MenuItem value="yesno">Yes / No</MenuItem>
										<MenuItem value="document">Document</MenuItem>
									</Select>
								</FormControl>
								<TextField
									fullWidth
									label={
										local.fieldType === 'yesno'
											? 'Question'
											: local.fieldType === 'document'
												? 'Document Label'
												: 'Field Label'
									}
									placeholder={
										local.fieldType === 'yesno'
											? 'e.g., Has the bank guarantee been received?'
											: local.fieldType === 'document'
												? 'e.g., Invoice, Progress Report'
												: 'e.g., Road completion length'
									}
									required
									size="small"
									value={local.label}
									onChange={(e) => updateProp('label', e.target.value)}
								/>
							</Stack>

							{/* Placeholder */}
							{local.fieldType !== 'document' && (
								<TextField
									fullWidth
									label="Placeholder Text"
									helperText="Hint shown inside the input when empty"
									size="small"
									value={local.placeholder || ''}
									onChange={(e) => updateProp('placeholder', e.target.value)}
								/>
							)}

							{/* Unit (number only) */}
							{local.fieldType === 'number' && (
								<TextField
									fullWidth
									label="Unit"
									helperText="e.g., km, USD, meters, kg"
									size="small"
									value={local.unit || ''}
									onChange={(e) => updateProp('unit', e.target.value)}
								/>
							)}

							{/* Options (dropdown only) */}
							{local.fieldType === 'select' && (
								<TextField
									fullWidth
									label="Dropdown Options"
									helperText="Enter one option per line"
									multiline
									minRows={3}
									maxRows={8}
									size="small"
									value={optionsText}
									onChange={(e) => setOptionsText(e.target.value)}
								/>
							)}

							{/* Max Documents (document only) */}
							{local.fieldType === 'document' && (
								<FormControl size="small" sx={{ maxWidth: 220 }}>
									<InputLabel>Max Documents</InputLabel>
									<Select
										label="Max Documents"
										value={local.maxDocuments ?? 1}
										onChange={(e) => updateProp('maxDocuments', Number(e.target.value))}>
										<MenuItem value={1}>1 (single)</MenuItem>
										<MenuItem value={2}>2</MenuItem>
										<MenuItem value={3}>3</MenuItem>
										<MenuItem value={5}>5</MenuItem>
										<MenuItem value={10}>10</MenuItem>
										<MenuItem value={0}>Unlimited</MenuItem>
									</Select>
								</FormControl>
							)}
						</Stack>
					</Box>

					{/* ============================================================= */}
					{/* SECTION 2: Behavior                                            */}
					{/* ============================================================= */}
					<Stack spacing={0.5}>
						<FormControlLabel
							sx={{ ml: 0 }}
							control={
								<Checkbox
									checked={local.required || false}
									onChange={(e) => updateProp('required', e.target.checked)}
									sx={{ p: 0.5, mr: 1 }}
								/>
							}
							label="Required — verifier must fill this in or provide an explanation"
						/>

						<FormControlLabel
							sx={{ ml: 0 }}
							control={
								<Checkbox
									checked={local.allowSupplierCompletion || false}
									onChange={(e) => updateProp('allowSupplierCompletion', e.target.checked)}
									sx={{ p: 0.5, mr: 1 }}
								/>
							}
							label="Allow completion by supplier"
						/>
						<Typography
							variant="body2"
							sx={{ color: 'text.secondary', pl: 4, fontSize: '1rem !important' }}>
							When enabled, suppliers can answer this field from the supplier portal. When
							disabled (default), only internal users can complete it.
						</Typography>
					</Stack>

					<Divider />

					{/* ============================================================= */}
					{/* SECTION 3: Expected Answer / Expectation Rules                 */}
					{/* ============================================================= */}
					<Box>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
							<IconRule sx={{ color: accentColor, fontSize: 20 }} />
							<Typography
								variant="overline"
								sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
								Expected Answer Rules
							</Typography>
						</Stack>
						<Typography
							variant="body2"
							sx={{ color: 'text.secondary', mb: 1, fontSize: '1.1rem !important' }}>
							If the verifier&apos;s answer doesn&apos;t match these rules, they&apos;ll need to
							provide an exception explanation.
						</Typography>

						{/* Existing rules list */}
						{rules.length > 0 && (
							<Stack spacing={1.5} sx={{ mb: 1 }}>
								{rules.map((rule, rIdx) => (
									<RuleEditor
										key={rIdx}
										rule={rule}
										fieldType={local.fieldType}
										availableOps={availableOps}
										selectOptions={
											local.fieldType === 'select'
												? optionsText
														.split('\n')
														.map((o) => o.trim())
														.filter(Boolean)
												: undefined
										}
										onChange={(patch) => updateRule(rIdx, patch)}
										onRemove={() => removeRule(rIdx)}
									/>
								))}
							</Stack>
						)}

						{/* Add rule button */}
						<Button
							onClick={addRule}
							size="small"
							startIcon={<IconAdd />}
							variant="outlined"
							sx={{ borderColor: accentColor, color: accentColor }}>
							{rules.length === 0 ? 'Add Expected Answer Rule' : 'Add Another Rule'}
						</Button>

						{rules.length === 0 && (
							<Typography
								variant="body2"
								sx={{
									color: 'text.disabled',
									mt: 1,
									fontStyle: 'italic',
									fontSize: '1.05rem !important',
								}}>
								No rules configured — any answer will be accepted.
							</Typography>
						)}

						{rules.length > 1 && (
							<Typography
								variant="body2"
								sx={{ color: 'text.secondary', mt: 0.5, fontSize: '1.05rem !important' }}>
								All rules must pass (AND logic). The first failed rule triggers the exception.
							</Typography>
						)}
					</Box>
				</Stack>
			</DialogContent>

			{/* ---------- Actions ---------- */}
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} variant="outlined">
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={!local.label.trim()}>
					Save Field
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// RuleEditor — edits a single V2ExpectationRule

interface RuleEditorProps {
	rule: ExpectationRule;
	fieldType: DataRequestFieldType;
	availableOps: ComparisonOperator[];
	selectOptions?: string[];
	onChange: (patch: Partial<ExpectationRule>) => void;
	onRemove: () => void;
}

const RuleEditor = ({
	rule,
	fieldType,
	availableOps,
	selectOptions,
	onChange,
	onRemove,
}: RuleEditorProps) => {
	const needsValue = !VALUE_LESS_OPS.includes(rule.operator);
	const needsSecondary = SECONDARY_VALUE_OPS.includes(rule.operator);
	const isList = LIST_OPS.includes(rule.operator);

	/** Value editor depends on field type + operator */
	const renderValueInput = () => {
		if (!needsValue) return null;

		// Yes/No — dropdown with yes/no
		if (fieldType === 'yesno') {
			if (isList) {
				// in / not_in for yesno — checkboxes
				const selected = Array.isArray(rule.value) ? rule.value.map(String) : [];
				return (
					<Stack direction="row" spacing={1}>
						{['yes', 'no'].map((opt) => (
							<FormControlLabel
								key={opt}
								control={
									<Checkbox
										checked={selected.includes(opt)}
										onChange={(e) => {
											const next = e.target.checked
												? [...selected, opt]
												: selected.filter((v: string) => v !== opt);
											onChange({ value: next });
										}}
									/>
								}
								label={opt.charAt(0).toUpperCase() + opt.slice(1)}
							/>
						))}
					</Stack>
				);
			}
			return (
				<FormControl fullWidth size="small">
					<InputLabel>Expected Value</InputLabel>
					<Select
						label="Expected Value"
						value={String(rule.value ?? '')}
						onChange={(e) => onChange({ value: e.target.value as string })}>
						<MenuItem value="yes">Yes</MenuItem>
						<MenuItem value="no">No</MenuItem>
					</Select>
				</FormControl>
			);
		}

		// Select (dropdown) — allow picking from defined options
		if (fieldType === 'select' && selectOptions && selectOptions.length > 0) {
			if (isList) {
				const selected = Array.isArray(rule.value) ? rule.value.map(String) : [];
				return (
					<FormControl fullWidth size="small">
						<InputLabel>Expected Values</InputLabel>
						<Select
							label="Expected Values"
							multiple
							value={selected}
							onChange={(e) => onChange({ value: e.target.value as string[] })}
							renderValue={(sel) => (sel as string[]).join(', ')}>
							{selectOptions.map((opt) => (
								<MenuItem key={opt} value={opt}>
									<Checkbox checked={selected.includes(opt)} size="small" sx={{ mr: 0.5 }} />
									{opt}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			}
			return (
				<FormControl fullWidth size="small">
					<InputLabel>Expected Value</InputLabel>
					<Select
						label="Expected Value"
						value={String(rule.value ?? '')}
						onChange={(e) => onChange({ value: e.target.value as string })}>
						{selectOptions.map((opt) => (
							<MenuItem key={opt} value={opt}>
								{opt}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		// Number field
		if (fieldType === 'number') {
			return (
				<Stack direction="row" spacing={1} sx={{ width: '100%' }}>
					<TextField
						fullWidth
						label={needsSecondary ? 'Minimum' : 'Expected Value'}
						type="number"
						size="small"
						value={rule.value ?? ''}
						onChange={(e) => {
							const v = e.target.value;
							onChange({ value: v === '' ? null : Number(v) });
						}}
					/>
					{needsSecondary && (
						<TextField
							fullWidth
							label="Maximum"
							type="number"
							size="small"
							value={rule.secondaryValue ?? ''}
							onChange={(e) => {
								const v = e.target.value;
								onChange({ secondaryValue: v === '' ? null : Number(v) });
							}}
						/>
					)}
				</Stack>
			);
		}

		// Date field
		if (fieldType === 'date') {
			return (
				<Stack direction="row" spacing={1} sx={{ width: '100%' }}>
					<TextField
						fullWidth
						label={needsSecondary ? 'Start Date' : 'Expected Date'}
						type="date"
						size="small"
						InputLabelProps={{ shrink: true }}
						value={String(rule.value ?? '')}
						onChange={(e) => onChange({ value: e.target.value || null })}
					/>
					{needsSecondary && (
						<TextField
							fullWidth
							label="End Date"
							type="date"
							size="small"
							InputLabelProps={{ shrink: true }}
							value={String(rule.secondaryValue ?? '')}
							onChange={(e) => onChange({ secondaryValue: e.target.value || null })}
						/>
					)}
				</Stack>
			);
		}

		// Text — free-form input
		return (
			<TextField
				fullWidth
				label={
					rule.operator === 'matches'
						? 'Regex Pattern'
						: isList
							? 'Expected Values (comma-separated)'
							: 'Expected Value'
				}
				helperText={
					rule.operator === 'matches'
						? 'Enter a valid regular expression pattern'
						: isList
							? 'Separate multiple values with commas'
							: undefined
				}
				size="small"
				value={
					isList
						? Array.isArray(rule.value)
							? rule.value.join(', ')
							: String(rule.value ?? '')
						: String(rule.value ?? '')
				}
				onChange={(e) => {
					if (isList) {
						onChange({
							value: e.target.value
								.split(',')
								.map((v) => v.trim())
								.filter(Boolean),
						});
					} else {
						onChange({ value: e.target.value || null });
					}
				}}
			/>
		);
	};

	return (
		<Paper
			elevation={0}
			sx={{
				border: '1px solid',
				borderColor: 'grey.200',
				borderRadius: 1,
				p: 1.5,
			}}>
			<Stack spacing={1.5}>
				{/* Condition + Value in one responsive row */}
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1}
					alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
					{/* Operator selector */}
					<FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
						<InputLabel>Condition</InputLabel>
						<Select
							label="Condition"
							value={rule.operator}
							onChange={(e) => {
								const newOp = e.target.value as ComparisonOperator;
								const patch: Partial<ExpectationRule> = { operator: newOp };
								if (VALUE_LESS_OPS.includes(newOp)) {
									patch.value = null;
									patch.secondaryValue = null;
								}
								onChange(patch);
							}}>
							{availableOps.map((op) => (
								<MenuItem key={op} value={op}>
									{OPERATOR_LABELS[op]}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* Value input (inline) */}
					<Box sx={{ flex: 1, minWidth: 0 }}>{renderValueInput()}</Box>

					{/* Remove button */}
					<IconButton
						onClick={onRemove}
						size="small"
						sx={{ color: 'grey.400', '&:hover': { color: 'error.main' }, alignSelf: 'center' }}>
						<IconDelete sx={{ fontSize: 18 }} />
					</IconButton>
				</Stack>

				{/* Custom failure message */}
				<TextField
					fullWidth
					label="Custom Exception Message (optional)"
					helperText="Overrides the default message when this rule fails"
					size="small"
					value={rule.message || ''}
					onChange={(e) => onChange({ message: e.target.value || undefined })}
				/>
			</Stack>
		</Paper>
	);
};

export default DialogFieldSettings;
